import React, { useCallback, useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import { Box, Stack } from "@mui/material"
import SideBarLayout from "src/v2/layout/side-bar-layout"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"
import { CurvasCreditoWorkflowService } from "src/services/curvas-credito-workflow-service"
import WorkflowForm from "./components/workflow-form"
import WorkflowRunsTable from "./components/workflow-runs-table"
import { TITLE_TRATAMENTO_SPREADS } from "./constants"

const CurvasCreditoWorkflowsPage = () => {
    const workflowService = useMemo(() => new CurvasCreditoWorkflowService(), [])
    const [workflowType, setWorkflowType] = useState("corp")
    const [dataInicial, setDataInicial] = useState(dayjs().subtract(1, "month").format("YYYY-MM-DD"))
    const [dataFinal, setDataFinal] = useState(dayjs().format("YYYY-MM-DD"))
    const [verticesFile, setVerticesFile] = useState(null)
    const [resumoFile, setResumoFile] = useState(null)
    const [useGpu, setUseGpu] = useState(true)
    const [runs, setRuns] = useState([])
    const [loading, setLoading] = useState(false)
    const [tableLoading, setTableLoading] = useState(false)
    const [uploadResetKey, setUploadResetKey] = useState(0)

    const resetFiles = useCallback(() => {
        setVerticesFile(null)
        setResumoFile(null)
        setUploadResetKey((key) => key + 1)
    }, [])

    const loadRuns = useCallback(async () => {
        setTableLoading(true)
        try {
            const response = await workflowService.listRuns({ workflowType, limit: 200 })
            const results = response && response.body && response.body.results
                ? response.body.results
                : []
            setRuns(results.map((run) => ({ ...run, id: run.id || run.external_workflow_id })))
        } catch (error) {
            console.error("Erro ao carregar workflows de Curvas Crédito", error)
            dispatchSnackbar("Erro ao carregar execuções", "error")
        } finally {
            setTableLoading(false)
        }
    }, [workflowService, workflowType])

    useEffect(() => {
        loadRuns()
        const interval = setInterval(loadRuns, 30000)
        return () => clearInterval(interval)
    }, [loadRuns])

    const handleWorkflowChange = (_event, value) => {
        setWorkflowType(value)
        setRuns([])
        resetFiles()
    }

    const handleDateChange = (setter) => (value) => {
        setter(dayjs(value).format("YYYY-MM-DD"))
    }

    const handleSubmit = async () => {
        if (!verticesFile || !resumoFile) {
            dispatchSnackbar("Selecione os arquivos de entrada", "warning")
            return
        }

        setLoading(true)
        try {
            await workflowService.startWorkflow({
                workflowType,
                dataInicial,
                dataFinal,
                verticesFile,
                resumoFile,
                useGpu,
            })
            dispatchSnackbar("Workflow iniciado", "success")
            resetFiles()
            await loadRuns()
        } catch (error) {
            const message = (
                error && error.response && error.response.text
            ) || (error && error.message) || "Erro ao iniciar workflow"
            dispatchSnackbar(message, "error")
        } finally {
            setLoading(false)
        }
    }

    const handleDownload = useCallback(async (downloadEndpoint) => {
        setLoading(true)
        try {
            const response = await workflowService.getDownload(downloadEndpoint)
            const url = response && response.body && response.body.download_url
            if (url) {
                window.open(url, "_blank", "noopener,noreferrer")
            }
        } catch (error) {
            dispatchSnackbar("Arquivo ainda não disponível", "warning")
        } finally {
            setLoading(false)
        }
    }, [workflowService])

    return (
        <SideBarLayout
            title={TITLE_TRATAMENTO_SPREADS}
            header
            variant="v2"
            loading={loading}
            headerImage="/background/eventos_headers.jpg"
            headerPosition="1% 42%"
            headerTitle={TITLE_TRATAMENTO_SPREADS}
        >
            <Box sx={{ mt: -2 }}>
                <Stack spacing={3}>
                    <WorkflowForm
                        workflowType={workflowType}
                        dataInicial={dataInicial}
                        dataFinal={dataFinal}
                        useGpu={useGpu}
                        loading={loading}
                        resetKey={uploadResetKey}
                        onWorkflowChange={handleWorkflowChange}
                        onDataInicialChange={handleDateChange(setDataInicial)}
                        onDataFinalChange={handleDateChange(setDataFinal)}
                        onVerticesFileChange={setVerticesFile}
                        onResumoFileChange={setResumoFile}
                        onUseGpuChange={setUseGpu}
                        onSubmit={handleSubmit}
                    />

                    <WorkflowRunsTable
                        rows={runs}
                        loading={tableLoading}
                        workflowType={workflowType}
                        onDownload={handleDownload}
                        onRefresh={loadRuns}
                    />
                </Stack>
            </Box>
        </SideBarLayout>
    )
}

export default CurvasCreditoWorkflowsPage
